import { ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import MaintenancePage from "@/pages/Maintenance";

/**
 * MaintenanceGate
 * Gdy `site_settings.maintenance_mode = true`:
 *   - admin (zalogowany z rolą admin) widzi normalną stronę,
 *   - wszyscy inni widzą stronę MaintenancePage.
 *
 * Wyjątki (zawsze działają, żeby admin mógł się zalogować):
 *   - /admin
 *   - /logowanie, /reset-hasla, /zapomnialem-hasla
 */
const ALWAYS_OPEN_PATHS = [
  "/admin",
  "/logowanie",
  "/reset-hasla",
  "/zapomnialem-hasla",
];

export const MaintenanceGate = ({ children }: { children: ReactNode }) => {
  const { isAdmin, loading: authLoading } = useAuth();
  const { data: settings, isLoading: settingsLoading } = useSiteSettings();
  const location = useLocation();

  const pathIsAlwaysOpen = ALWAYS_OPEN_PATHS.some(
    (p) => location.pathname === p || location.pathname.startsWith(p + "/")
  );

  if (pathIsAlwaysOpen) return <>{children}</>;

  // Dopóki nie wiemy, czy maintenance jest aktywny - pokazujemy normalną stronę
  // (uniknięcie błysku landingu dla normalnych userów; admin i tak dostanie po chwili dane).
  // Ale jeśli flaga jest aktywna i jeszcze nie wiemy czy user jest adminem - wolimy
  // pokazać landing zamiast publicznej strony.
  if (settingsLoading || !settings) return <>{children}</>;

  if (!settings.maintenance_mode) return <>{children}</>;

  // Maintenance aktywny - poczekaj aż auth się załaduje, żeby admin nie zobaczył landingu
  if (authLoading) return <MaintenancePage message={settings.maintenance_message} />;

  if (isAdmin) return <>{children}</>;

  return <MaintenancePage message={settings.maintenance_message} />;
};
